import { useMemo, useState } from "react";
import { Clock3, Mail, UserRoundCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@workspace/ui/components/ui/input";
import { bookingsApi, BookingApiError, type TripData } from "@/lib/bookings-api";

type PrimaryDriver = NonNullable<TripData["primaryDriver"]>;

interface Props {
  token: string;
  publicId: string;
  state: PrimaryDriver;
  onChanged: () => Promise<void> | void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PrimaryDriverSelectionCard({
  token,
  publicId,
  state,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const repairStorageKey = `bac-primary-driver-repair:${publicId}`;
  const repairEmail = (() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(repairStorageKey) || "null");
      return typeof saved?.email === "string" ? saved.email : "";
    } catch {
      return "";
    }
  })();
  const [repairPending, setRepairPending] = useState(!!repairEmail);
  const [nominating, setNominating] = useState(
    !!repairEmail || (state.choice === "nominated" && !state.invitation),
  );
  const [email, setEmail] = useState(state.invitation?.email ?? repairEmail);
  const [saving, setSaving] = useState<"renter" | "nominated" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const expiryLabel = useMemo(() => {
    if (!state.invitation?.expiresAt) return null;
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(state.invitation.expiresAt));
  }, [state.invitation?.expiresAt]);

  const chooseRenter = async () => {
    setSaving("renter");
    setError(null);
    try {
      await bookingsApi.setPrimaryDriver(token, publicId, { renterIsDriver: true });
      await onChanged();
    } catch (err) {
      setError(err instanceof BookingApiError ? err.message : "We couldn't save your choice. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  const invite = async () => {
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      setError("Enter a valid email address.");
      return;
    }
    setSaving("nominated");
    setError(null);
    try {
      await bookingsApi.setPrimaryDriver(token, publicId, {
        renterIsDriver: false,
        email: normalized,
      });
      try { sessionStorage.removeItem(repairStorageKey); } catch { /* storage is best-effort */ }
      setRepairPending(false);
      await onChanged();
      setNominating(false);
    } catch (err) {
      setRepairPending(true);
      setError(err instanceof BookingApiError ? err.message : "We couldn't send the invitation. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  if (state.choice === "renter") {
    return (
      <div className="mt-7 rounded-2xl border border-brand-bright/30 bg-brand-bright/10 p-5" data-testid="primary-driver-renter">
        <div className="flex gap-3">
          <UserRoundCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-deep" aria-hidden="true" />
          <div>
            <h2 className="font-semibold text-foreground">{t("booking.primary_driver.renter_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("booking.primary_driver.renter_body")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const invitation = state.invitation;
  if (state.choice === "nominated" && invitation && !nominating) {
    const complete = invitation.status === "confirmed";
    return (
      <div className="mt-7 rounded-2xl border border-border bg-card p-5" data-testid="primary-driver-waiting">
        <div className="flex gap-3">
          {complete ? (
            <UserRoundCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-deep" aria-hidden="true" />
          ) : (
            <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <h2 className="font-semibold text-foreground">
              {complete
                ? t("booking.primary_driver.ready_title")
                : invitation.isExpired
                  ? t("booking.primary_driver.expired_title")
                  : t("booking.primary_driver.waiting_title")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {complete
                ? t("booking.primary_driver.complete_body", { email: invitation.email })
                : t("booking.primary_driver.invite_body", { email: invitation.email })}
            </p>
            {!complete && expiryLabel && !invitation.isExpired ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {t("booking.primary_driver.expires", { date: expiryLabel })}
              </p>
            ) : null}
            {!complete && state.clearanceDeadline ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {t("booking.primary_driver.clearance_deadline", {
                  date: new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium", timeStyle: "short",
                  }).format(new Date(state.clearanceDeadline)),
                })}
              </p>
            ) : null}
          </div>
        </div>
        {!complete ? (
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              data-testid="button-primary-driver-resend"
              disabled={saving !== null}
              onClick={() => { setEmail(invitation.email); void invite(); }}
            >
              <Mail aria-hidden="true" className="h-4 w-4" />
              {invitation.isExpired
                ? t("booking.primary_driver.send_new")
                : t("booking.primary_driver.resend")}
            </Button>
            <Button type="button" variant="ghost" disabled={saving !== null} onClick={() => void chooseRenter()}>
              {t("booking.primary_driver.switch_to_renter")}
            </Button>
          </div>
        ) : null}
        {error ? <p role="alert" className="mt-3 text-sm text-destructive">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className="mt-7 rounded-2xl border border-border bg-card p-5" data-testid="primary-driver-choice">
      <h2 className="text-xl font-semibold text-foreground">{t("booking.primary_driver.question")}</h2>
      {repairPending ? (
        <p role="status" data-testid="status-primary-driver-repair" className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          {t("booking.primary_driver.repair_body", {
            publicId,
            defaultValue: `Your booking (${publicId}) is saved, but the driver invitation was not sent. Retry it here; you do not need to book again.`,
          })}
        </p>
      ) : null}
      <p className="mt-2 text-sm text-muted-foreground">
        {t("booking.primary_driver.question_body")}
      </p>
      {!nominating ? (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Button
            type="button"
            data-testid="button-primary-driver-yes"
            disabled={saving !== null}
            onClick={() => void chooseRenter()}
          >
            {t("booking.primary_driver.yes")}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="button-primary-driver-no"
            disabled={saving !== null}
            onClick={() => setNominating(true)}
          >
            {t("booking.primary_driver.no")}
          </Button>
        </div>
      ) : (
        <div className="mt-5">
          <label htmlFor="primary-driver-email" className="text-sm font-medium text-foreground">
            {t("booking.primary_driver.email_label")}
          </label>
          <Input
            id="primary-driver-email"
            data-testid="input-primary-driver-email"
            type="email"
            value={email}
            autoComplete="email"
            className="mt-2"
            placeholder={t("booking.primary_driver.email_placeholder")}
            onChange={(event) => setEmail(event.target.value)}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            {t("booking.primary_driver.email_help")}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              data-testid="button-primary-driver-send"
              disabled={saving !== null}
              onClick={() => void invite()}
            >
              {saving === "nominated"
                ? t("booking.primary_driver.sending")
                : t("booking.primary_driver.send")}
            </Button>
            <Button type="button" variant="ghost" disabled={saving !== null} onClick={() => setNominating(false)}>
              {t("booking.primary_driver.back")}
            </Button>
          </div>
        </div>
      )}
      {error ? <p role="alert" className="mt-3 text-sm text-destructive">{error}</p> : null}
    </div>
  );
}